import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { auth, onAuthStateChanged, reload } from './firebase.js';
import { Me } from './api.js';

const AuthContext = createContext(null);

const DEMO = import.meta.env.VITE_DEMO_ADMIN === '1';
const demoUser = DEMO
  ? { uid: 'demo-admin', email: 'demo@blaze.local', displayName: 'Demo Admin', emailVerified: true }
  : null;

const ADMIN_ROLES = new Set(['admin', 'superadmin']);

// Mirror shared/canDo — kept in the web bundle to avoid the shared workspace.
function canDo(permissions, service, action, role) {
  if (role === 'superadmin') return true; // superadmin bypasses the map
  if (!permissions) return true; // legacy admins without the field → full access
  if (permissions.all) return true;
  const p = permissions[service];
  if (!p) return false;
  return action === 'read' ? p.includes('r') : p.includes('w');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(demoUser);
  const [role, setRole] = useState(DEMO ? 'admin' : null);
  const [emailVerified, setEmailVerified] = useState(DEMO);
  const [permissions, setPermissions] = useState(DEMO ? { all: true } : undefined);
  const [loading, setLoading] = useState(!DEMO);

  useEffect(() => {
    if (DEMO) return;
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setRole(null);
        setEmailVerified(false);
        setPermissions(undefined);
        setLoading(false);
        return;
      }
      const token = await fbUser.getIdTokenResult();
      setUser(fbUser);
      setRole(token.claims.role || 'member');
      setEmailVerified(!!fbUser.emailVerified);
      setLoading(false);
      // Load permissions from Firestore user doc (only meaningful for admins).
      if (ADMIN_ROLES.has(token.claims.role)) {
        try {
          const r = await Me.get();
          setPermissions(r.user?.permissions);
        } catch {
          setPermissions(undefined);
        }
      } else {
        setPermissions({});
      }
    });
    return unsub;
  }, []);

  const refreshRole = useCallback(async () => {
    if (!auth.currentUser) return;
    const token = await auth.currentUser.getIdTokenResult(true);
    setRole(token.claims.role || 'member');
    if (ADMIN_ROLES.has(token.claims.role)) {
      try {
        const r = await Me.get();
        setPermissions(r.user?.permissions);
      } catch {
        setPermissions(undefined);
      }
    }
  }, []);

  const refreshPermissions = useCallback(async () => {
    try {
      const r = await Me.get();
      setPermissions(r.user?.permissions);
    } catch {
      /* noop */
    }
  }, []);

  const refreshVerification = useCallback(async () => {
    if (!auth.currentUser) return false;
    await reload(auth.currentUser);
    setEmailVerified(!!auth.currentUser.emailVerified);
    return auth.currentUser.emailVerified;
  }, []);

  const can = useCallback(
    (service, action = 'read') => canDo(permissions, service, action, role),
    [permissions, role],
  );

  const value = useMemo(
    () => ({
      user,
      role,
      emailVerified,
      permissions,
      loading,
      can,
      refreshRole,
      refreshPermissions,
      refreshVerification,
    }),
    [user, role, emailVerified, permissions, loading, can, refreshRole, refreshPermissions, refreshVerification],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
